import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Card, CardBody } from '@/components/ui/card';

export function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordMode, setPasswordMode] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const errorParam = params.get('error');

  const requestLink = useMutation({
    mutationFn: (email: string) => api.post<{ ok: boolean }>('/api/auth/request-login', { email }),
    onSuccess: () => {
      setError(null);
      setLinkSent(true);
    },
    onError: (err: ApiError) => setError(err.message || 'Could not request a login link.'),
  });

  const passwordLogin = useMutation({
    mutationFn: () =>
      api.post<{ operator: unknown }>('/api/auth/login', {
        email: email.trim().toLowerCase(),
        password,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/', { replace: true });
    },
    onError: (err: ApiError) => setError(err.message || 'Wrong email or password.'),
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-sm space-y-4">
        <div className="text-center">
          <div className="text-lg font-semibold text-slate-900">Control Plane</div>
          <div className="text-xs text-slate-500 mt-0.5">Operator sign-in</div>
        </div>
        <Card>
          <CardBody className="space-y-4">
            {errorParam === 'missing_token' && (
              <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">
                The login link is missing its token. Request a new one below.
              </div>
            )}
            {error && (
              <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>
            )}
            {linkSent ? (
              <div className="space-y-2">
                <p className="text-sm text-slate-700">
                  If <b>{email}</b> is a registered operator, a login link is on its way.
                </p>
                <p className="text-xs text-slate-500">
                  Check your inbox. The link is valid for 15 minutes.
                </p>
                <Button
                  variant="outline"
                  className="w-full mt-2"
                  onClick={() => {
                    setLinkSent(false);
                    setError(null);
                  }}
                >
                  Back
                </Button>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!email) return;
                  if (passwordMode) {
                    if (password) passwordLogin.mutate();
                  } else {
                    requestLink.mutate(email.trim().toLowerCase());
                  }
                }}
                className="space-y-3"
              >
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    autoFocus
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>
                {passwordMode && (
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      required
                      autoFocus
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                )}
                {passwordMode ? (
                  <>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={passwordLogin.isPending || !email || !password}
                    >
                      {passwordLogin.isPending ? 'Signing in…' : 'Sign in with password'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setPassword('');
                        setError(null);
                        setPasswordMode(false);
                      }}
                    >
                      Send login link instead
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={requestLink.isPending || !email}
                    >
                      {requestLink.isPending ? 'Sending…' : 'Send login link'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setError(null);
                        setPasswordMode(true);
                      }}
                    >
                      Login with password
                    </Button>
                  </>
                )}
              </form>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export function VerifyPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const token = params.get('token') || '';
  const [state, setState] = useState<'verifying' | 'ok' | 'error'>('verifying');
  const [message, setMessage] = useState('Signing you in…');

  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage('Login link is missing its token.');
      return;
    }
    let cancelled = false;
    api
      .post<{ operator: unknown }>('/api/auth/verify-login', { token })
      .then(() => {
        if (cancelled) return;
        qc.invalidateQueries({ queryKey: ['me'] });
        setState('ok');
        navigate('/', { replace: true });
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        setState('error');
        setMessage(err.message || 'Login failed. Request a new link.');
      });
    return () => {
      cancelled = true;
    };
  }, [token, navigate, qc]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-sm">
        <CardBody className="space-y-3 text-center">
          <div className="text-sm font-semibold text-slate-900">
            {state === 'verifying' ? 'Signing you in' : state === 'ok' ? 'Signed in' : 'Login failed'}
          </div>
          <div className="text-xs text-slate-600">{message}</div>
          {state === 'error' && (
            <Button variant="outline" onClick={() => navigate('/login')} className="w-full">
              Back to login
            </Button>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
